import React, { useState, useRef } from 'react';
import { AnyAction } from '@reduxjs/toolkit';
import { MailDelivery, exists } from 'lib/types';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { ColumnButton } from 'lib/components/ColumnButton';
import { getMailProviderFromNoticeOrDraft } from 'lib/affidavits';
import { useAppSelector } from 'redux/hooks';
import { getFirebaseContext } from 'utils/firebase';
import { selectDraftSnap } from 'redux/placement';
import { AffidavitMailingAddressForm } from './AffidavitMailingAddressForm';
import { actions as affidavitRecipientActions } from './deliveryRecipients.slice';

const { addMailingRecipient } = affidavitRecipientActions;

type MailingRecipientManagerProps = {
  recipients: MailDelivery[];
  dispatch: React.Dispatch<AnyAction>;
  errors: string[];
  showErrors: boolean;
};

export function MailingRecipientManager({
  dispatch,
  recipients,
  errors,
  showErrors
}: MailingRecipientManagerProps) {
  const noticeDraft = useAppSelector(selectDraftSnap);
  const [addingRecipients, setAddingRecipients] = useState(false);

  const nextId = useRef(0);
  const keyMapRef = useRef<number[]>([]);
  while (keyMapRef.current.length < recipients.length) {
    keyMapRef.current.push(nextId.current++);
  }
  if (keyMapRef.current.length > recipients.length) {
    keyMapRef.current.length = recipients.length;
  }
  return (
    <div className="my-2">
      {recipients.map((mailingAddress, index) => {
        return (
          <AffidavitMailingAddressForm
            key={keyMapRef.current[index]}
            mailingAddress={mailingAddress}
            dispatch={dispatch}
            index={index}
            error={errors[index]}
            showErrors={showErrors}
            onDelete={() => {
              keyMapRef.current.splice(index, 1);
              dispatch(affidavitRecipientActions.deleteMailingRecipient(index));
            }}
          />
        );
      })}
      <ColumnButton
        id="addMoreRecipients"
        secondary
        link
        startIcon={<PlusCircleIcon className="w-5 h-5" />}
        buttonText="Add more recipients"
        onClick={async () => {
          setAddingRecipients(true);
          if (!exists(noticeDraft)) return;
          const mailProvider = await getMailProviderFromNoticeOrDraft(
            getFirebaseContext(),
            noticeDraft
          );
          dispatch(
            addMailingRecipient({
              mailProvider
            })
          );
          setAddingRecipients(false);
        }}
        disabled={!exists(noticeDraft)}
        loading={addingRecipients}
        type="button"
      />
    </div>
  );
}
